<template>
  <div
    v-if="blok"
    v-editable="blok"
    class="w-full"
    :class="[
      marginClasses,
      {
        'aspect-[16/9]': blok.aspectRatio === '16/9',
        'aspect-[4/3]': blok.aspectRatio === '4/3',
        'aspect-[1/1]': blok.aspectRatio === '1/1',
      },
    ]"
  >
    <div ref="video" />
  </div>
</template>

<script lang="ts">
import getYouTubeID from 'get-youtube-id'
import YouTubePlayer from 'youtube-player'
import useStoryblokMargins from '~/cms/storyblok/composables/useStoryblokMargins'
import type { SbYouTubeVideo } from '~/cms/storyblok/types/storyblok.d.ts'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'

const playerStatuses: { [key: string]: string } = {
  '-1': 'unstarted',
  0: 'ended',
  1: 'playing',
  2: 'paused',
  3: 'buffering',
  5: 'video cued',
}

type YoutubeEvent = {
  data: number
}

export default defineComponent({
  name: 'YouTubeVideo',
  props: {
    blok: {
      type: Object as PropType<SbYouTubeVideo>,
      default: () => {},
    },
  },
  setup(props) {
    const { trackYouTube } = useTrackingEvents()
    const currentShop = useCurrentShop()
    const { marginClasses } = useStoryblokMargins(props.blok)
    const video = ref<HTMLElement | null>(null)

    const id = computed(
      () => getYouTubeID(props.blok.link || '') || props.blok.link,
    )

    let player: any = null

    const handleStateChange = (event: CustomEvent<any> & YoutubeEvent) => {
      if ([0, 1, 2].includes(event.data)) {
        const status = playerStatuses[event.data]
        if (!status) {
          return
        }
        trackYouTube({
          id: id.value || '',
          title: props.blok.title || '',
          status,
          creative_slot: props.blok.creative_slot,
          creative_name: props.blok.creative_name,
        })
      }
    }

    onMounted(() => {
      if (video.value) {
        player = YouTubePlayer(video.value, {
          videoId: id.value,
          height: '100%',
          width: '100%',
          host: 'https://www.youtube-nocookie.com',
          playerVars: {
            hl: currentShop.value.isoLanguageCode,
            rel: 0,
          },
        })
        player.on('stateChange', handleStateChange)
      }
    })

    onBeforeUnmount(() => {
      player = null
    })

    return {
      video,
      marginClasses,
    }
  },
})
</script>
